import { useIsomorphicLayoutEffect } from 'framer-motion';
import { useRef } from 'react';
import { useStickToBottom } from 'use-stick-to-bottom';
import { useOnScrollToTop } from '~/lib/hooks/useOnScrollToTop';
import type { Message } from '~/types/chat';

type ScrollRefType = ReturnType<typeof useStickToBottom>['scrollRef'];

interface Props {
  scrollRef: ScrollRefType;
  loadMoreMessages: VoidFunction;
  disabled: boolean;
  messages: Message[] | undefined;
}

export const useInfiniteChatScroll = ({ scrollRef, loadMoreMessages, disabled, messages }: Props) => {
  const previousScrollHeightRef = useRef(0);

  const shouldPersistScrollRef = useRef(false);

  useOnScrollToTop({
    ref: scrollRef,
    onScrollToTop: async () => {
      previousScrollHeightRef.current = scrollRef.current?.scrollHeight ?? 0;

      shouldPersistScrollRef.current = true;

      await loadMoreMessages();
    },
    disabled,
  });

  useIsomorphicLayoutEffect(() => {
    if (!scrollRef?.current || !shouldPersistScrollRef.current) {
      return;
    }

    const prevScrollHeight = previousScrollHeightRef.current;
    const newScrollHeight = scrollRef.current.scrollHeight;
    const newScrollTop = scrollRef.current.scrollTop;

    if (newScrollTop === 0) {
      scrollRef.current?.scrollTo({
        top: Math.max(newScrollHeight - prevScrollHeight - 64, 0),
      });
    }

    shouldPersistScrollRef.current = false;
  }, [messages?.length]);
};
